import { Tab, TabGroup } from '@humanforce/mango-ui-react';
import { useNavigate, useParams } from 'react-router';
import { resolveItemByPath } from '../navigation/navConfig';
import { PageTitle } from '../components/layout/PageTitle';
import { OrganisationDetailsScreen } from '../features/organisation/OrganisationDetailsScreen';
import { PlaceholderPanel } from './PlaceholderPage';

interface TabbedItemPageProps {
  /** Absolute item route path, e.g. "/maintenance/organisation". */
  itemPath: string;
}

/**
 * Renders a navigation item that has sub-items as a Mango tab strip.
 * Tabs are URL-addressable: selecting a tab navigates to `${itemPath}/${slug}`,
 * and the active tab is derived from the `:tab` route param, so deep links work.
 */
export const TabbedItemPage = ({ itemPath }: TabbedItemPageProps) => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const resolved = resolveItemByPath(itemPath);
  const subItems = resolved?.item.subItems;
  if (!resolved || !subItems?.length) return null;

  const { item } = resolved;
  const activeSlug = subItems.some((s) => s.slug === tab) ? (tab as string) : subItems[0].slug;

  return (
    <>
      <PageTitle title={item.label} subtitle={item.note} />
      <TabGroup value={activeSlug} onValueChange={(value) => { if (value !== activeSlug) navigate(`${itemPath}/${value}`); }}>
        {subItems.map((subItem) => (
          <Tab key={subItem.slug} value={subItem.slug} label={subItem.label}>
            {subItem.content === 'organisationDetails'
              ? <OrganisationDetailsScreen />
              : <PlaceholderPanel name={`${item.label} — ${subItem.label}`} />}
          </Tab>
        ))}
      </TabGroup>
    </>
  );
};
